import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { User, Trophy, Sword, Users, Heart, BookOpen, Clock, Coins, ChevronRight, Shield } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const UserProfilePage = () => {
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    loadProfile();
  }, [userId]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/users/${userId}/profile-stats`);
      setProfile(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Error loading profile");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-purple-950/30 to-black pt-24 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-t-yellow-500 border-purple-700 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-purple-950/30 to-black pt-24 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-500 mb-4">Error</h2>
          <p className="text-gray-400">{error}</p>
          <Link to="/" className="mt-4 inline-block text-yellow-400 hover:text-yellow-300">
            Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const { user, stats, decks, tournament_history, recent_transactions } = profile;

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-purple-950/30 to-black pt-24 pb-12" data-testid="user-profile-page">
      <div className="container mx-auto px-4">
        {/* Profile Header */}
        <div className="glass-card rounded-2xl p-6 mb-8">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            {/* Avatar */}
            <div className="relative">
              <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-yellow-500 bg-purple-900">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User size={48} className="text-gray-500" />
                  </div>
                )}
              </div>
              <div className="absolute -bottom-2 -right-2 bg-yellow-500 rounded-full p-2">
                <Trophy size={20} className="text-black" />
              </div>
            </div>

            {/* User Info */}
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-3xl font-black text-white mb-2">{user.name}</h1>
              <p className="text-gray-400 mb-4">{user.email}</p>
              
              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard icon={<BookOpen size={20} />} value={stats.decks_count} label="Decks" color="yellow" />
                <StatCard icon={<Users size={20} />} value={stats.followers} label="Seguidores" color="purple" />
                <StatCard icon={<Heart size={20} />} value={stats.friends} label="Amigos" color="pink" />
                <StatCard icon={<Coins size={20} />} value={user.nircoin_balance?.toFixed(2) || "0.00"} label="NirCoins" color="green" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {["overview", "decks", "tournaments", "transactions"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-full font-semibold text-sm whitespace-nowrap transition-all ${
                activeTab === tab
                  ? "bg-yellow-500 text-black"
                  : "bg-purple-900/50 text-gray-400 hover:text-white"
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab === "overview" && "Resumen"}
              {tab === "decks" && `Decks (${stats.decks_count})`}
              {tab === "tournaments" && "Torneos"}
              {tab === "transactions" && "NirCoins"}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="glass-card rounded-2xl p-6">
          {activeTab === "overview" && (
            <OverviewTab stats={stats} tournament_history={tournament_history} decks={decks} />
          )}
          {activeTab === "decks" && (
            <DecksTab decks={decks} />
          )}
          {activeTab === "tournaments" && (
            <TournamentsTab history={tournament_history} />
          )}
          {activeTab === "transactions" && (
            <TransactionsTab transactions={recent_transactions} balance={user.nircoin_balance} />
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon, value, label, color }) => {
  const colorMap = {
    yellow: "from-yellow-500/20 to-amber-600/20 border-yellow-500/50",
    purple: "from-purple-500/20 to-indigo-600/20 border-purple-500/50",
    pink: "from-pink-500/20 to-rose-600/20 border-pink-500/50",
    green: "from-green-500/20 to-emerald-600/20 border-green-500/50",
  };

  return (
    <div className={`bg-gradient-to-br ${colorMap[color]} border rounded-xl p-3 text-center`}>
      <div className="flex justify-center mb-1 text-gray-400">{icon}</div>
      <p className="text-xl font-bold text-white">{value}</p>
      <p className="text-xs text-gray-400">{label}</p>
    </div>
  );
};

const OverviewTab = ({ stats, tournament_history, decks }) => (
  <div className="space-y-6">
    {/* Recent Activity */}
    <div>
      <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
        <Clock size={20} className="text-yellow-500" />
        Actividad Reciente
      </h3>
      <div className="grid md:grid-cols-2 gap-4">
        {/* Recent Decks */}
        <div className="bg-purple-900/30 rounded-xl p-4">
          <h4 className="text-sm font-bold text-gray-400 mb-3">Últimos Decks</h4>
          {decks.slice(0, 3).map((deck) => (
            <Link key={deck.id} to={`/decks/${deck.id}`} className="flex items-center gap-3 py-2 border-b border-purple-700/30 last:border-0 hover:bg-purple-800/30 rounded px-2 transition-colors">
              <BookOpen size={16} className="text-yellow-500" />
              <span className="text-white font-medium">{deck.name}</span>
              <span className="text-xs text-gray-500 ml-auto">{deck.format}</span>
            </Link>
          ))}
          {decks.length === 0 && <p className="text-gray-500 text-sm">No hay decks aún</p>}
        </div>

        {/* Tournament Performance */}
        <div className="bg-purple-900/30 rounded-xl p-4">
          <h4 className="text-sm font-bold text-gray-400 mb-3">Posiciones en Rankings</h4>
          {tournament_history.slice(0, 3).map((t, idx) => (
            <div key={idx} className="flex items-center gap-3 py-2 border-b border-purple-700/30 last:border-0">
              <Trophy size={16} className={t.position <= 3 ? "text-yellow-500" : "text-gray-500"} />
              <span className="text-white font-medium">#{t.position}</span>
              <span className="text-xs text-gray-400">{t.collection}</span>
              <span className="text-xs text-yellow-400 ml-auto">{t.total_points} pts</span>
            </div>
          ))}
          {tournament_history.length === 0 && <p className="text-gray-500 text-sm">Sin historial de torneos</p>}
        </div>
      </div>
    </div>

    {/* Format Stats */}
    {tournament_history.length > 0 && (
      <div>
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Shield size={20} className="text-yellow-500" />
          Puntos por Formato
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { name: "GOAT", key: "goat_points", color: "from-green-500 to-emerald-600" },
            { name: "Edison", key: "edison_points", color: "from-yellow-500 to-amber-600" },
            { name: "HAT", key: "hat_points", color: "from-purple-500 to-indigo-600" },
            { name: "Speed Duel", key: "speed_duel_points", color: "from-blue-500 to-cyan-600" },
            { name: "Total", key: "total_points", color: "from-pink-500 to-rose-600" },
          ].map((format) => {
            const totalPoints = tournament_history.reduce((sum, t) => sum + (t[format.key] || 0), 0);
            return (
              <div key={format.key} className={`bg-gradient-to-br ${format.color} rounded-xl p-4 text-center`}>
                <p className="text-2xl font-black text-white">{totalPoints}</p>
                <p className="text-xs text-white/70">{format.name}</p>
              </div>
            );
          })}
        </div>
      </div>
    )}
  </div>
);

const DecksTab = ({ decks }) => (
  <div>
    <div className="flex justify-between items-center mb-4">
      <h3 className="text-xl font-bold text-white">Mis Decks</h3>
      <Link to="/decks/new" className="px-4 py-2 bg-yellow-500 text-black rounded-lg font-semibold hover:bg-yellow-400 transition-colors">
        + Nuevo Deck
      </Link>
    </div>
    
    {decks.length === 0 ? (
      <div className="text-center py-12">
        <BookOpen size={48} className="mx-auto text-gray-600 mb-4" />
        <p className="text-gray-400">No tienes decks guardados</p>
        <Link to="/decks/new" className="mt-4 inline-block text-yellow-400 hover:text-yellow-300">
          Crear mi primer deck
        </Link>
      </div>
    ) : (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {decks.map((deck) => (
          <Link key={deck.id} to={`/decks/${deck.id}`} className="bg-purple-900/30 rounded-xl p-4 hover:bg-purple-800/40 transition-colors group">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-bold text-white group-hover:text-yellow-400 transition-colors">{deck.name}</h4>
              {deck.is_public && <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">Público</span>}
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                deck.format === "goat" ? "bg-green-500/20 text-green-400" :
                deck.format === "edison" ? "bg-yellow-500/20 text-yellow-400" :
                deck.format === "hat" ? "bg-purple-500/20 text-purple-400" :
                deck.format === "speed_duel" ? "bg-blue-500/20 text-blue-400" :
                "bg-gray-500/20 text-gray-400"
              }`}>
                {deck.format?.toUpperCase()}
              </span>
            </div>
            <ChevronRight size={20} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-600 group-hover:text-yellow-400 transition-colors" />
          </Link>
        ))}
      </div>
    )}
  </div>
);

const TournamentsTab = ({ history }) => (
  <div>
    <h3 className="text-xl font-bold text-white mb-4">Historial de Torneos</h3>
    
    {history.length === 0 ? (
      <div className="text-center py-12">
        <Trophy size={48} className="mx-auto text-gray-600 mb-4" />
        <p className="text-gray-400">Sin historial de torneos</p>
      </div>
    ) : (
      <div className="space-y-3">
        {history.map((t, idx) => (
          <div key={idx} className="bg-purple-900/30 rounded-xl p-4 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center font-black text-xl ${
              t.position === 1 ? "bg-yellow-500 text-black" :
              t.position === 2 ? "bg-gray-300 text-black" :
              t.position === 3 ? "bg-amber-600 text-white" :
              "bg-purple-700 text-white"
            }`}>
              #{t.position}
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-white">{t.collection}</h4>
              <div className="flex gap-4 text-xs text-gray-400 mt-1">
                {t.goat_points > 0 && <span>GOAT: {t.goat_points}</span>}
                {t.edison_points > 0 && <span>Edison: {t.edison_points}</span>}
                {t.hat_points > 0 && <span>HAT: {t.hat_points}</span>}
                {t.speed_duel_points > 0 && <span>Speed: {t.speed_duel_points}</span>}
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-black text-yellow-400">{t.total_points}</p>
              <p className="text-xs text-gray-500">puntos</p>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const TransactionsTab = ({ transactions, balance }) => (
  <div>
    <div className="flex justify-between items-center mb-4">
      <h3 className="text-xl font-bold text-white">NirCoins</h3>
      <div className="text-right">
        <p className="text-3xl font-black text-green-400">{balance?.toFixed(2) || "0.00"}</p>
        <p className="text-xs text-gray-500">Balance actual</p>
      </div>
    </div>
    
    {transactions.length === 0 ? (
      <div className="text-center py-12">
        <Coins size={48} className="mx-auto text-gray-600 mb-4" />
        <p className="text-gray-400">Sin transacciones</p>
      </div>
    ) : (
      <div className="space-y-2">
        {transactions.map((t, idx) => (
          <div key={idx} className="bg-purple-900/30 rounded-lg p-3 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              t.amount > 0 ? "bg-green-500/20" : "bg-red-500/20"
            }`}>
              <Coins size={18} className={t.amount > 0 ? "text-green-400" : "text-red-400"} />
            </div>
            <div className="flex-1">
              <p className="text-white font-medium">{t.description}</p>
              <p className="text-xs text-gray-500">{t.type}</p>
            </div>
            <div className="text-right">
              <p className={`font-bold ${t.amount > 0 ? "text-green-400" : "text-red-400"}`}>
                {t.amount > 0 ? "+" : ""}{t.amount}
              </p>
              <p className="text-xs text-gray-500">Balance: {t.balance_after}</p>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default UserProfilePage;
